import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Flex } from '@chakra-ui/react';

import { Table, TableFilter } from '@app/components/common/table';
import { useAlert, useApi } from '@app/hooks';
import { StandsItem, StandsReport, StandsReportItem, StandsTotal } from '@app/api/models';
import { API_FEAT_DATE_FORMAT } from '@app/utils';
import { DateRangeField, Paper, Txt } from '@app/components/common';
import { DateRange } from '@app/components/common/date-range-picker/types';
import Dialog from '@app/components/common/modals/dialog';
import { Level } from '@app/api/models/structure';
import { TableBase } from '@app/components/common/table/table-base';

import { useColumns } from './columns';
import { standsColumns } from './stands-columns';
import { StandsReportFilter, StandsNodeItem } from './types';
import { getArrayFilterValue, getFilterValue } from '../utils/filter';

export const StandsReportTable = () => {
  const api = useApi();
  const { success } = useAlert();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<DateRange>({
    startDate: moment().subtract(1, 'day'),
    endDate: moment()
  });
  const [data, setData] = useState<StandsReport>({
    total: {
      check: 0,
      mount: 0,
    },
    report_data: {},
  });
  const [levels, setLevels] = useState<Level[]>([]);
  const [chain, setChain] = useState<StandsNodeItem[]>([]);
  const [total, setTotal] = useState<StandsTotal>({check: 0, mount: 0});
  const [filter, setFilter] = useState<TableFilter<StandsNodeItem>[]>([
    { name: 'full_name', label: 'Пользователь', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'director', label: 'Директор', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'regional_manager', label: 'Региональный менеджер', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'territorial_manager', label: 'Территориальный менеджер', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'distributor', label: 'Дистрибьютор', type: 'autocomplete', value: '', source: 'distributor', scope: 'include' },
    { name: 'route_number', label: 'Номер маршрута', type: 'autocomplete-paginated', value: '', source: 'route_number', scope: 'include' },
    {
      name: 'outlet', label: 'Торговая точка', type: 'outlet', value: '', scope: 'include',
      source: ''
    },
    { name: 'stand_id', label: 'ID стойки', type: 'text', value: '', source: '', scope: 'include' },
    { name: 'mount_dt', label: 'Дата установки', type: 'date', value: '', source: '', scope: 'include' },
    { name: 'update_dt', label: 'Дата обновления', type: 'date', value: '', source: '', scope: 'include' },
  ]);

  const [showDetail, setShowDetail] = useState<StandsNodeItem>();

  const fetchData = useCallback(async ({ period, filter }: {
    period: DateRange,
    filter: TableFilter<StandsNodeItem>[]
  }) => { 
    setLoading(true);
    const { data: levelsData } = await api.structure.getLevels({
      filter: {
        is_active: 'true',
      },
      sort: '-value',
      limit: 10,
      offset: 0,
    });

    setLevels([...levelsData.data, 
      {
        id: -1,
        name: 'Outlets',
        value: -1,
        is_active: true,
      }
    ]);
    setChain([]);

    const { data } = await api.reports.getStandsReport({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
        full_name: getFilterValue(filter, 'full_name'),
        manufacturer_id: getArrayFilterValue<StandsNodeItem>(filter, 'manufacturer'),
        model_id: getArrayFilterValue<StandsNodeItem>(filter, 'model'),
        brand_id: getArrayFilterValue<StandsNodeItem>(filter, 'brand'),
        equip_model_id: getArrayFilterValue<StandsNodeItem>(filter, 'equip_model'),
        director: getFilterValue<StandsNodeItem>(filter, 'director'),
        regional_manager: getFilterValue<StandsNodeItem>(filter, 'regional_manager'),
        territorial_manager: getFilterValue<StandsNodeItem>(filter, 'territorial_manager'),
        distributor_id: getFilterValue<StandsNodeItem, number>(filter, 'distributor'),
        route_number_id: getFilterValue<StandsNodeItem, number>(filter, 'route_number'),
        outlet_id: getFilterValue<StandsNodeItem, number>(filter, 'outlet'),
        stand_id: getFilterValue<StandsNodeItem, number>(filter, 'stand_id'),
        mount_dt: getFilterValue<StandsNodeItem>(filter, 'mount_dt'),
        update_dt: getFilterValue<StandsNodeItem>(filter, 'update_dt'),
      },
      download: false
    });
    setData(data);
    setTotal(data?.total || { check: 0, mount: 0 });
    setLoading(false);
  }, []);

  const tableData = useMemo(() => {
    if (!data) {return [];}
    if (chain.length === 0) {
      return [...Object.keys(data.report_data).map<StandsNodeItem>((key) => {
        return {
          id: key,
          title: data.report_data[key].employee?.name || 'Вакансия',
          subtitle: data.report_data[key].territory,
          level: data.report_data[key].level,
          total: data.report_data[key].total,
        };
      }),
        {
          id: '-1',
          title: 'Итого',
          subtitle: '',
          level: levels[0],
          total: data.total,
        }
      ];
    }

    let currentNode: StandsReportItem = data.report_data[chain[0].id];

    for (const item of chain.slice(1)) {
      currentNode = currentNode.subordinates[item.id];
    }
    
    return [
      ...chain, 
      ...Object.keys(currentNode.subordinates ||  {}).map<StandsNodeItem>((key) => ({
        id: key,
        title: key == currentNode.subordinates[key].level.name ? '' : currentNode.subordinates[key].employee?.name || 'Вакансия',
        subtitle: key == currentNode.subordinates[key].level.name ? '[пусто]' :  currentNode.subordinates[key].territory,
        level: currentNode.subordinates[key].level,
        total: currentNode.subordinates[key].total,
      })),
      ...Object.keys(currentNode.outlet || {}).map<StandsNodeItem>((key) => {
        return {
          id: key,
          title: `[${key}] ` +  (currentNode.outlet[key]?.outlet?.name || 'ТТ неизвестно'),
          subtitle: currentNode.outlet[key]?.full_address || '-',
          level: levels[levels.length - 1],
          total: {
            check: currentNode.outlet[key]?.total.check || 0,
            mount: currentNode.outlet[key]?.total.mount || 0,
          },
        };
      }),
      {
        id: '-1',
        title: 'Итого',
        subtitle: '',
        level: levels[0],
        total: data.total,
      }
    ]; 
  }, [data, chain, levels]);

  const onFilterChange = useCallback((filter: TableFilter<StandsNodeItem>[]) => {
    setFilter(filter);
    fetchData({ period, filter });
  }, [fetchData, period]);

  const onItemClick = useCallback((item: StandsNodeItem) => {
    setChain([...chain, item]);
  }, [chain]);

  const onMoveUpClick = useCallback((item: StandsNodeItem) => {
    const index = chain.findIndex(({ id }) => id === item.id);
    setChain(chain.slice(0, index));
  }, [chain]);

  const onShowDetail = useCallback((item: StandsNodeItem) => {
   setShowDetail(item);
  }, []);

  const columns = useColumns({ chain, levels, onItemClick, onMoveUpClick, onShowDetail });


  const onChangePeriod = useCallback((period: DateRange) => {
    setPeriod(period);
    fetchData({ period, filter });
  }, [fetchData, filter]);

  const download = useCallback(async () => {
    setLoading(true);
    const { code, message } = await api.reports.getStandsReport({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
        full_name: getFilterValue(filter, 'full_name'),
        manufacturer_id: getArrayFilterValue<StandsNodeItem>(filter, 'manufacturer'),
        model_id: getArrayFilterValue<StandsNodeItem>(filter, 'model'),
        brand_id: getArrayFilterValue<StandsNodeItem>(filter, 'brand'),
        equip_model_id: getArrayFilterValue<StandsNodeItem>(filter, 'equip_model'),
        director: getFilterValue<StandsNodeItem>(filter, 'director'),
        regional_manager: getFilterValue<StandsNodeItem>(filter, 'regional_manager'),
        territorial_manager: getFilterValue<StandsNodeItem>(filter, 'territorial_manager'),
        distributor_id: getFilterValue<StandsNodeItem, number>(filter, 'distributor'),
        route_number_id: getFilterValue<StandsNodeItem, number>(filter, 'route_number'),
        outlet_id: getFilterValue<StandsNodeItem, number>(filter, 'outlet'),
        stand_id: getFilterValue<StandsNodeItem, number>(filter, 'stand_id'),
        mount_dt: getFilterValue<StandsNodeItem>(filter, 'mount_dt'),
        update_dt: getFilterValue<StandsNodeItem>(filter, 'update_dt'),
      },
      download: true
    });
    if (code === 200) {
      success(message);
    }
    setLoading(false);
  }, [period, filter]); 

  const scanningData = useMemo(() => {
    if (!showDetail) {return [];}

    const addOutlet = (outlet: EntityBase, scans: StandsItem[]) => {
      return scans.map((item) => ({ ...item, stands: { ...item, outlet } }));
    };

    const recur = (item?: StandsReportItem) => {
      if (item?.subordinates) {return Object.values(item.subordinates).map(recur).flat();}
      if (item?.outlet) {
        return Object.values(item.outlet).map(({ stands, outlet }) => addOutlet(outlet, stands)).flat();
      }
      return [];
    };

    if (chain.length === 0 || showDetail.id === chain[0].id) { return recur(data.report_data[showDetail.id]); }

    let currentNode: StandsReportItem = data.report_data[chain[0].id];

    for (const item of chain.slice(1)) {
      if (item.level.id === -1 || item.id === showDetail.id) { break; }
      currentNode = currentNode.subordinates[item.id];
    }

    if (showDetail.level.id === -1) {
      return addOutlet(currentNode.outlet[showDetail.id].outlet, currentNode.outlet[showDetail.id].stands);
    }


    currentNode = currentNode.subordinates[showDetail.id];


    return recur(currentNode);
  }, [data, chain, showDetail]);

  return <>
    <Box mb={2}>
      <Paper>
        <Box px={6} py={4} display="flex">
          <Box mr={2} minWidth={300}>
            <DateRangeField
              id='period'
              label="Период"
              value={period}
              onChange={onChangePeriod}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
    <TableBase<StandsNodeItem> 
      title="Отчет по стойкам"  
      columns={columns}
      data={tableData}
      loading={loading}
      onDownload={download}
      filter={filter}
      onFilterChange={onFilterChange}
    />
    <Dialog open={!!showDetail}
      onClose={() => setShowDetail(undefined)}
      title="Отчет по стойкам" 
      width={800}
    >
      <Box margin={-4}>
        <Table<StandsItem>
          title=""
          columns={standsColumns}
          data={scanningData}
          paper={false}
        />
      </Box>
    </Dialog>
  </>;
};